import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { showErrorToast } from '@/api/errorHandler'
import {
  PAYOUT_DOCUMENT_ACCEPT,
  PAYOUT_DOCUMENT_MAX_SIZE_MB,
  PAYOUT_TEXT_MAX_LENGTH,
  validatePayoutDocument,
} from '@/lib/payoutDocument'
import { Upload, FileText, X } from 'lucide-react'
import { useLocalization } from '@/contexts/LocalizationContext'

type InvoiceUploadAction = 'approve' | 'dispute'

interface InvoiceUploadFormProps {
  action: InvoiceUploadAction
  statementId: string
  onSubmit: (payload: {
    statementId: string
    /** Supporting document — required by the backend on both actions. */
    file: File
    reason?: string
    notes?: string
  }) => Promise<void>
  onCancel: () => void
  disabled?: boolean
}

export function InvoiceUploadForm({
  action,
  statementId,
  onSubmit,
  onCancel,
  disabled = false,
}: InvoiceUploadFormProps) {
  const { t } = useLocalization()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [reason, setReason] = useState('')
  const [notes, setNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const title = action === 'approve' ? t('financials.approvePayoutStatement') : t('financials.provideReasonToDispute')
  const subtitle =
    action === 'approve'
      ? t('financials.approveSubtitle')
      : t('financials.disputeSubtitle')

  // Both actions require a document. Dispute additionally requires a reason.
  const isValid = useMemo(() => {
    if (!selectedFile) return false
    if (action === 'approve') return notes.length <= PAYOUT_TEXT_MAX_LENGTH
    const r = reason.trim()
    return r.length > 0 && r.length <= PAYOUT_TEXT_MAX_LENGTH
  }, [action, notes, reason, selectedFile])

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Mirrors the backend rules so a bad pick is rejected before upload.
    const error = validatePayoutDocument(file)
    if (error) {
      setSelectedFile(null)
      event.target.value = ''
      showErrorToast(
        t(`financials.${error}`, {
          max: PAYOUT_DOCUMENT_MAX_SIZE_MB,
          types: PAYOUT_DOCUMENT_ACCEPT,
        })
      )
      return
    }

    setSelectedFile(file)
  }

  const handleSubmit = async () => {
    if (disabled || isSubmitting) return

    if (!selectedFile) {
      showErrorToast(t('financials.document_required'))
      return
    }

    if (action === 'approve' && notes.length > PAYOUT_TEXT_MAX_LENGTH) {
      showErrorToast(t('financials.notesMaxLength', { max: PAYOUT_TEXT_MAX_LENGTH }))
      return
    }

    if (action === 'dispute') {
      const r = reason.trim()
      if (!r) {
        showErrorToast(t('financials.reasonRequired'))
        return
      }
      if (r.length > PAYOUT_TEXT_MAX_LENGTH) {
        showErrorToast(t('financials.reasonMaxLength', { max: PAYOUT_TEXT_MAX_LENGTH }))
        return
      }
    }

    setIsSubmitting(true)
    try {
      await onSubmit({
        statementId,
        file: selectedFile,
        reason: action === 'dispute' ? reason.trim() : undefined,
        notes: action === 'approve' ? notes.trim() || undefined : undefined,
      })
    } catch (e) {
      // Error is toasted by caller in most cases, but keep a safe fallback.
      showErrorToast(e as Error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
        <h4 className="mb-1 flex items-center font-semibold text-orange-800">
          <Upload className="me-2 h-4 w-4" />
          {title}
        </h4>
        <p className="text-sm text-orange-700">{subtitle}</p>
      </div>

      <div className="space-y-3">
        <div>
          <label className="mb-2 block text-sm font-medium">
            {t('financials.supportingDocumentRequired')}
          </label>
          <Input
            type="file"
            onChange={handleFileSelect}
            disabled={disabled || isSubmitting}
            accept={PAYOUT_DOCUMENT_ACCEPT}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {t('financials.documentConstraints', {
              types: PAYOUT_DOCUMENT_ACCEPT,
              max: PAYOUT_DOCUMENT_MAX_SIZE_MB,
            })}
          </p>
          {selectedFile && (
            <div className="mt-2 flex items-center text-sm text-muted-foreground">
              <FileText className="me-1 h-4 w-4" />
              {selectedFile.name}
            </div>
          )}
        </div>

        {action === 'approve' && (
          <div>
            <label className="mb-2 block text-sm font-medium">{t('financials.notesOptional')}</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('financials.addApprovalNotes')}
              rows={3}
              maxLength={PAYOUT_TEXT_MAX_LENGTH}
              disabled={disabled || isSubmitting}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {notes.length} / {PAYOUT_TEXT_MAX_LENGTH}
            </p>
          </div>
        )}

        {action === 'dispute' && (
          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('financials.reasonRequiredMax', { max: PAYOUT_TEXT_MAX_LENGTH })}
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('financials.enterReasonForDispute')}
              rows={4}
              maxLength={PAYOUT_TEXT_MAX_LENGTH}
              disabled={disabled || isSubmitting}
              className="resize-y"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {reason.length} / {PAYOUT_TEXT_MAX_LENGTH}
            </p>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
          <X className="me-1 h-4 w-4" />
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!isValid || disabled || isSubmitting}
          className={action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
        >
          <Upload className="me-1 h-4 w-4" />
          {isSubmitting
            ? t('common.processing')
            : action === 'approve'
              ? t('financials.approve')
              : t('financials.dispute')}
        </Button>
      </div>
    </div>
  )
}
